/**
 * Solution Concept Repository - Platform Persistence layer.
 *
 * Public interface for SolutionConceptStatement artifact persistence. All
 * reads and writes go through this module. No caller touches Supabase
 * directly for solution concept artifacts.
 *
 * Write path:
 *   writeSolutionConcept()
 *     → supabase.rpc("write_solution_concept_artifact")  [atomic: version + both INSERTs]
 *     → reads RETURNING (version, created_at)
 *     → assembles ArtifactEnvelope from authoritative database values
 *     → constructArtifact(envelope, content) → SolutionConceptStatement
 *     → retries up to MAX_VERSION_RETRIES times on UNIQUE constraint violation
 *
 * Read path:
 *   readSolutionConceptByVersion / readLatestSolutionConcept / readSolutionConceptLineage
 *     → joined select on pi_artifact_envelopes + solution_concept_statement_contents
 *     → rowToSolutionConcept() reassembles typed SolutionConceptStatement
 *
 * Note: SolutionConceptContent.displayName is part of the content type itself.
 * No separate displayName parameter is required - the repository extracts it
 * from the content argument.
 *
 * Responsibility boundary:
 *   This module owns: RPC invocation, retry logic, RETURNING assembly,
 *   constructArtifact() call, error translation, public repository interface.
 *   Database layer owns: version arithmetic, timestamp generation, atomicity.
 *   Governance layer owns: artifact identity semantics, type contracts.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArtifactEnvelope } from "@/types/platformArtifactTypes";
import type { SolutionConceptContent, SolutionConceptStatement } from "@/intelligence/strategicPITypes";
import { constructArtifact } from "@/platform/artifactConstructor";
import { logger } from "@/system/logger";

const MAX_VERSION_RETRIES = 3;
const UNIQUE_VIOLATION_CODE = "23505";

// ─── Private helpers ──────────────────────────────────────────────────────────

type SolutionConceptEnvelopeWithContent = {
  version:    number;
  created_at: string;
  solution_concept_statement_contents: {
    display_name:      string;
    description:       string;
    value_proposition: string;
    target_audience:   string;
    confidence:        number;
    differentiators:   unknown;
    core_problems:     unknown;
  } | null;
};

function rowToSolutionConcept(
  projectId: string,
  row: SolutionConceptEnvelopeWithContent,
): SolutionConceptStatement | null {
  const c = row.solution_concept_statement_contents;
  if (!c) return null;

  const envelope: ArtifactEnvelope = {
    projectId,
    artifactType: "solution_concept_statement",
    version:      row.version,
    createdAt:    row.created_at,
  };

  const content: SolutionConceptContent = {
    displayName:      c.display_name,
    description:      c.description,
    valueProposition: c.value_proposition,
    targetAudience:   c.target_audience,
    confidence:       c.confidence,
    differentiators:  c.differentiators as SolutionConceptContent["differentiators"],
    coreProblems:     c.core_problems   as SolutionConceptContent["coreProblems"],
  };

  return constructArtifact(envelope, content);
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a new SolutionConceptStatement artifact version atomically.
 *
 * The RPC assigns the next contiguous version via MAX+1 inside the same
 * transaction that inserts both rows. On UNIQUE constraint violation (concurrent
 * writers landing on the same version), this function retries up to
 * MAX_VERSION_RETRIES times before throwing.
 */
export async function writeSolutionConcept(
  projectId: string,
  content:   SolutionConceptContent,
): Promise<SolutionConceptStatement> {
  let attempt = 0;

  while (attempt < MAX_VERSION_RETRIES) {
    const { data, error } = await supabase.rpc("write_solution_concept_artifact", {
      p_project_id:        projectId,
      p_display_name:      content.displayName,
      p_description:       content.description,
      p_value_proposition: content.valueProposition,
      p_target_audience:   content.targetAudience,
      p_confidence:        content.confidence,
      p_differentiators:   content.differentiators,
      p_core_problems:     content.coreProblems,
    });

    if (!error) {
      const row = (data as Array<{ version: number; created_at: string }>)[0];
      const envelope: ArtifactEnvelope = {
        projectId,
        artifactType: "solution_concept_statement",
        version:      row.version,
        createdAt:    row.created_at,
      };
      return constructArtifact(envelope, content);
    }

    if ((error as { code?: string }).code === UNIQUE_VIOLATION_CODE) {
      attempt++;
      logger.warn("SolutionConceptStatement version conflict, retrying", { projectId, attempt });
      continue;
    }

    logger.error("writeSolutionConcept RPC failed", { projectId, error });
    throw new Error(`writeSolutionConcept failed: ${error.message}`);
  }

  throw new Error(
    `writeSolutionConcept exhausted ${MAX_VERSION_RETRIES} retries for project ${projectId}`,
  );
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Read a specific SolutionConceptStatement version. Returns null if the version
 * does not exist for this project.
 */
export async function readSolutionConceptByVersion(
  projectId: string,
  version:   number,
): Promise<SolutionConceptStatement | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, solution_concept_statement_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "solution_concept_statement")
    .eq("version", version)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    logger.error("readSolutionConceptByVersion failed", { projectId, version, error });
    throw new Error(`readSolutionConceptByVersion failed: ${error.message}`);
  }

  return rowToSolutionConcept(projectId, data as unknown as SolutionConceptEnvelopeWithContent);
}

/**
 * Read the latest SolutionConceptStatement version for a project. Returns null
 * if no solution concept statements exist for the project.
 */
export async function readLatestSolutionConcept(
  projectId: string,
): Promise<SolutionConceptStatement | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, solution_concept_statement_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "solution_concept_statement")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error("readLatestSolutionConcept failed", { projectId, error });
    throw new Error(`readLatestSolutionConcept failed: ${error.message}`);
  }

  if (!data) return null;
  return rowToSolutionConcept(projectId, data as unknown as SolutionConceptEnvelopeWithContent);
}

/**
 * Read all SolutionConceptStatement versions for a project in ascending version
 * order. Returns an empty array if no solution concepts exist for the project.
 */
export async function readSolutionConceptLineage(
  projectId: string,
): Promise<SolutionConceptStatement[]> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, solution_concept_statement_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "solution_concept_statement")
    .order("version", { ascending: true });

  if (error) {
    logger.error("readSolutionConceptLineage failed", { projectId, error });
    throw new Error(`readSolutionConceptLineage failed: ${error.message}`);
  }

  return (data as unknown as SolutionConceptEnvelopeWithContent[])
    .map(row => rowToSolutionConcept(projectId, row))
    .filter((d): d is SolutionConceptStatement => d !== null);
}
